/**
 * WS-F §3.3 — the Rollout reads and writes.
 *
 * ## ⛔ SIX ROUTES ACROSS THREE CONTROLLERS, AND WS-F §2 DOES NOT SAY WHICH
 *
 * Verified against HEAD, because §2's release-engine table lists the routes without naming
 * their controllers and the natural guess is wrong:
 *
 * | Route | Gate | Controller |
 * |---|---|---|
 * | `GET  /v1/admin/apps/:appId/release-chain`      | `cp:app:adoption:read`  | site-admin-api `AppAdminController.releaseChain` |
 * | `GET  /v1/admin/apps/:appId/affected-dps`       | `cp:app:adoption:read`  | site-admin-api `AppAdminController.affectedDps` |
 * | `GET  /v1/apps/:appId/rollout-diagnostics`      | `cp:app:read`           | release-engine `AppsController.getRolloutDiagnostics` |
 * | `GET  /v1/apps/:appId/channel-pins`             | `cp:app:read`           | release-engine `AppChannelPinsController.list` |
 * | `GET  /v1/apps/:appId/channel-pins/history`     | `cp:app:read`           | release-engine `AppPromotionController.history` |
 * | `POST /v1/apps/:appId/promote`                   | `cp:app:promote-channel`| release-engine `AppPromotionController.promote` |
 * | `POST /v1/apps/:appId/demote`                    | `cp:app:demote-channel` | release-engine `AppPromotionController.demote` |
 * | `DELETE /v1/apps/:appId/channel-pins/:ring`     | `cp:app:channel:pin`    | release-engine `AppPromotionController.unpin` |
 *
 * ⛔ **`GET channel-pins` and `DELETE channel-pins/:ring` live on DIFFERENT controllers with
 * DIFFERENT path grammars.** The GET is on `@Controller('v1/apps/:appId/channel-pins')` and its
 * `PUT` sibling takes a **`:channelId` uuid**; the DELETE is on
 * `@Controller('v1/apps/:appId')` as `channel-pins/:ring` and takes a **literal ring name**.
 * Sending a channelId to the unpin route 404s. WS-F §5 asked for the channel-pins gates to be
 * read off the controller; this table is that answer.
 *
 * ⛔ **THREE DISTINCT WRITE GRANTS** (§6 assertion 7): promote, demote and unpin are separately
 * gated, so a principal who may promote but not unpin must see exactly that. They are exported
 * as named constants below so the pane cannot gate two controls on one string by accident.
 */
import type {
  AffectedDpsView,
  AppAdoptionMatrix,
  AppAdoptionRow,
  AppRolloutDiagnostics,
  ReleaseChainView,
  Ring,
} from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

export type { AffectedDpsView, AppAdoptionMatrix, AppAdoptionRow, AppRolloutDiagnostics, ReleaseChainView };

/**
 * The three write permissions, and the read that governs the ladder.
 *
 * ⛔ Separate exported constants rather than one array, because §6 assertion 7's mutation is
 * *"gate all three on one permission"* — and that mutation is much harder to write by accident
 * when each control names its own constant at its own call site.
 *
 * ⚠️ §2.1: the resource leg of `useHasPermission` is EXACT-MATCH, no wildcards. Gating a
 * runtime panel on `cp:app:read` does not work, and neither does gating promote on
 * `cp:app:write`.
 */
export const PROMOTE_PERMISSION = 'cp:app:promote-channel';
export const DEMOTE_PERMISSION = 'cp:app:demote-channel';
export const UNPIN_PERMISSION = 'cp:app:channel:pin';
/**
 * ⛔ PIN IS NOT UNPIN, ON EITHER AXIS (R7 WS-4 §1.5).
 *
 * `cp:app:write` pins; `cp:app:channel:pin` unpins — two different permissions on two halves of
 * one control, verified on HEAD (`AppChannelPinsController.set` vs
 * `AppPromotionController.unpin`). A real principal can hold either without the other, so
 * **Pin and Unpin hide independently** and ⛔ a shared gate would hide a control the operator
 * holds. WS-4 A11 asserts all three arms (write-only, pin-only, neither).
 */
export const PIN_PERMISSION = 'cp:app:write';
/** The ladder + affected-DP preview read. ⛔ NOT the same grant as the runtime panels (§2.1). */
export const ADOPTION_PERMISSION = 'cp:app:adoption:read';

/**
 * ⛔⛔ **THE CHANNEL-PINS READ'S GATE — `cp:app:read`, RESOURCE-SCOPED. IT IS NOT
 * AUTHENTICATED-ONLY.**
 *
 * ⚠️ **06b §8.3 states the opposite** — *"already exported (`getChannelPins`),
 * authenticated-only (K1)"* — and that premise is **measurably false**. Verified at HEAD:
 * `AppChannelPinsController.list` in release-engine is
 * `@Get()` + `@RequireResourcePermission('cp:app:read', 'appId')` under `@UseGuards(CpJwtAuthGuard)`.
 * The `cp:app:write` on `AppChannelPinsController.set` is the PIN, a different verb on the same controller.
 *
 * ⛔ **Declared here because a module's gate set is what `permission-gate-parity` reasons over.**
 * Before this constant, `appRollout.api`'s only `:read`-shaped export was {@link ADOPTION_PERMISSION},
 * so ANY component importing this module was held to owe `cp:app:adoption:read` — even one that
 * imports it solely for {@link getChannelPins}. `AppAccessPane` (06b G6 · M-10) is that component,
 * and the ratchet reported it as an ungated read whose only "fix" would have been to claim a grant
 * its route does not require: a `ForbiddenState` naming `cp:app:adoption:read` for a 403 the adoption
 * ladder never issued.
 *
 * ⭐ Strictly LOOSENING and factual: it adds a true value to the module's gate set, so no existing
 * consumer's discharge changes and the ratchet can only shrink.
 */
export const CHANNEL_PINS_PERMISSION = 'cp:app:read';

/**
 * The read gate of `GET /v1/apps/:appId/rollout-diagnostics`: `cp:app:read` on the app
 * (`AppsController.getRolloutDiagnostics`). The Rollout tab names it in the diagnostics denial, so
 * the denial names the grant of its own route and not the channel-pins one that has the same value.
 */
export const ROLLOUT_DIAGNOSTICS_PERMISSION = 'cp:app:read';

/**
 * One channel's pin.
 *
 * ⛔ HAND-DECLARED: `AppChannelPinView` is release-engine SERVICE-LOCAL
 * (`app-channel-pins.service.ts`) and has no `@zion/sdk-contracts` entry, so there is
 * nothing to import. Producer chain: `AppChannelPinsController.list` → `listForApp`.
 *
 * ⚠️ **`pinnedAt` is `string`, and the producer's type says `Date | null`.** That is not a
 * mismatch — it is the JSON boundary: the column is `mode: 'date'`, so the service really does
 * hold a `Date`, and `JSON.stringify` emits an ISO string. Declaring `Date` here would
 * typecheck against the producer and be **wrong at runtime** — `pin.pinnedAt.getTime()` would
 * throw on a string. Always read a wire type at the wire, not at the service.
 */
export interface AppChannelPin {
  readonly channelId: string;
  /** `stable` | `beta` | `alpha` | `edge` — the four global channels, always all four. */
  readonly channelName: string;
  readonly pinnedVersionId: string | null;
  readonly pinnedAppVersion: string | null;
  /** ISO-8601. See the `Date`-at-the-wire note above. */
  readonly pinnedAt: string | null;
  readonly pinnedByOperatorId: string | null;
}

/**
 * One pin-history entry — the audit trail.
 *
 * ⛔ HAND-DECLARED for the same reason: `AppChannelPinHistoryRow` is a Drizzle
 * `$inferSelect` on `release-engine`'s own schema (`db/schema/app-channel-pins.ts`), which
 * is a server type over a server table, not a published wire contract.
 *
 * ⚠️ `changedAt` is `string` here and `Date` on the producer — same JSON boundary as above.
 */
export interface AppPinHistoryEntry {
  readonly id: string;
  readonly appId: string;
  readonly ring: string;
  readonly appVersionId: string | null;
  readonly changedByOperator: string;
  /** ISO-8601. */
  readonly changedAt: string;
  /**
   * ⛔ FIVE values, from the table's own CHECK constraint (`app_channel_pin_history_action_check`):
   * `pin` · `promote` · `demote` · `unpin` · `edge-auto-bump`. `edge-auto-bump` is the one a
   * hand-written union would miss — it is a machine actor, and rendering it as an operator
   * action would attribute an automatic bump to whoever last touched the app.
   */
  readonly action: string;
  readonly fromAppVersionId: string | null;
  readonly note: string | null;
}

/** ⛔ `promote` requires `expectedAppVersion` — an optimistic-concurrency check, not a hint. */
export interface PromoteRequest {
  readonly fromRing: Ring;
  readonly toRing: Ring;
  /**
   * The version the caller BELIEVES is on `fromRing`. The server rejects a mismatch, which is
   * what stops a promote firing against a ladder that moved while the confirmation dialog was
   * open — the exact race §3.3 calls "the single most consequential control in this slice".
   */
  readonly expectedAppVersion: string;
  readonly note?: string;
}

export interface DemoteRequest {
  readonly ring: Ring;
  readonly expectedAppVersion: string;
  readonly note?: string;
}

export function releaseChainPath(appId: string): string {
  return `/v1/admin/apps/${appId}/release-chain`;
}

export function getReleaseChain(appId: string): Promise<ReleaseChainView> {
  return api.get<ReleaseChainView>(releaseChainPath(appId));
}

/**
 * The promote/demote preview.
 *
 * ⚠️ `ring` is a REQUIRED query param on this route (`AppAdminController.affectedDps`, `?ring=`) —
 * the preview is *"who would a change into THIS ring affect"*, so there is no fleet-wide form.
 */
export function affectedDpsPath(appId: string, ring: Ring): string {
  return `/v1/admin/apps/${appId}/affected-dps?ring=${encodeURIComponent(ring)}`;
}

export function getAffectedDps(appId: string, ring: Ring): Promise<AffectedDpsView> {
  return api.get<AffectedDpsView>(affectedDpsPath(appId, ring));
}

/**
 * R7 WS-4 E58 — the tenant × version × ring adoption matrix for one app.
 *
 * ⚠️ FOLDED INTO THIS FILE rather than into a new `adoption.api.ts` (lead ruling ACK-ws0-8). WS-4
 * §6 assigned E58 to a new module alongside E59/E60/E67/E68 — but all four of those were ALREADY
 * implemented here, so creating it would have been the "two modules issuing the same reads" hazard
 * §6 forbids two lines later. E58 was the only genuinely unwrapped read of the five.
 *
 * ⛔ Same service as `release-chain` and `affected-dps` (site-admin-api `/v1/admin/apps`), same
 * `cp:app:adoption:read` resource grant on `appId` — which is the argument for it living beside
 * them rather than in a module of its own.
 *
 * ⚠️ `appId` is validated by `ParseUUIDPipe` at the door (`AppAdminController.adoption`), so a slug
 * is a 400 rather than a miss — independently enforcing R6 D-19's uuid-not-slug rule.
 */
export interface AdoptionQuery {
  readonly ring?: Ring;
  readonly version?: string;
  readonly health?: 'healthy' | 'degraded' | 'unhealthy';
  readonly orgId?: string;
  /**
   * The opaque keyset cursor from the PREVIOUS page's `cursor` field.
   *
   * ⛔ PASSED BACK VERBATIM. It is a base64url token encoding a 4-part keyset
   * (site-admin-api `encodeAdoptionCursor`); ⛔ do not parse it, do not construct one, do not assume it is a number
   * or that it is ordered. `cursor === null` on the response means that page was the last.
   */
  readonly cursor?: string;
  /** Page size `[1, 200]`, default 50 (the `limit` query of `AppAdminController.adoption`). */
  readonly limit?: number;
}

/**
 * ⚠️ Built with `URLSearchParams`, and that is what keeps the cursor intact.
 *
 * `URLSearchParams` percent-encodes with form semantics, and **base64url's alphabet
 * (`A-Za-z0-9-_`) contains no character it alters** — `-` and `_` are unreserved. So the cursor
 * arrives at the server byte-identical without any special-casing here. ⛔ Do NOT "fix" this by
 * hand-encoding or by stripping padding: base64url has no `+`/`/` to escape and no `=` padding to
 * remove, and a transform that is a no-op today becomes a corruption the day the server changes
 * its token alphabet. WS-4 A4 asserts the exact round-tripped string.
 *
 * Keys are emitted only when supplied, so an unfiltered request sends a bare path — a `?` with no
 * pairs would still match the route but makes every test assertion carry a meaningless suffix.
 */
export function adoptionPath(appId: string, query: AdoptionQuery = {}): string {
  const params = new URLSearchParams();
  if (query.ring !== undefined) params.set('ring', query.ring);
  if (query.version !== undefined) params.set('version', query.version);
  if (query.health !== undefined) params.set('health', query.health);
  if (query.orgId !== undefined) params.set('orgId', query.orgId);
  if (query.cursor !== undefined) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs === ''
    ? `/v1/admin/apps/${appId}/adoption`
    : `/v1/admin/apps/${appId}/adoption?${qs}`;
}

export function getAdoption(appId: string, query: AdoptionQuery = {}): Promise<AppAdoptionMatrix> {
  return api.get<AppAdoptionMatrix>(adoptionPath(appId, query));
}

export function channelPinsPath(appId: string): string {
  return `/v1/apps/${appId}/channel-pins`;
}

export function getChannelPins(appId: string): Promise<readonly AppChannelPin[]> {
  return api.get<readonly AppChannelPin[]>(channelPinsPath(appId));
}

/**
 * Pin history, newest first.
 *
 * ⛔ **UNCHANGED, and that is load-bearing twice over.** It is the `useApiResource`
 * CACHE KEY of the history read in `AppOverviewPane` and `AppRolloutPane`, not just a
 * URL — changing the emitted query string would change the key and silently
 * re-fetch. And it always sends `limit`, which is what makes the response shape
 * single-valued (DECISIONS R-24 D-7).
 *
 * `cursor` (plan 2026-10-13-console-ward-app-drill-in, A-5) is the `nextCursor` of the page
 * before, for the Rollout tab's Show more. The client sends it back as the server minted it,
 * percent-encoded, and never parses it. The route answers 400 `invalid cursor` for a token it did
 * not mint. With no `cursor` the path is byte-identical to the first-page path, so the cache key
 * of the first page does not move.
 *
 * ⚠️ **`limit` is NO LONGER hand-clamped.** This docblock used to say the controller
 * parsed it with `Math.max(1, Math.min(500, Number(limit) || 50))` — that was stale
 * twice over: the clamp is gone, and the route now **400s** an out-of-range or
 * non-numeric `limit` against `appPinHistoryQuerySchema` (WS-1 §3.1). This client
 * only ever sends a number in `[1, 500]`, so it cannot reach the 400.
 */
export function pinHistoryPath(appId: string, limit = 50, cursor?: string): string {
  const firstPage = `/v1/apps/${appId}/channel-pins/history?limit=${limit}`;
  return cursor === undefined ? firstPage : `${firstPage}&cursor=${encodeURIComponent(cursor)}`;
}

/** One page of pin history, as the server's V-1 envelope. */
export interface PinHistoryPage {
  readonly items: readonly AppPinHistoryEntry[];
  readonly nextCursor: string | null;
  /** ⛔ *More entries existed than you received* — DECISIONS R-4's one meaning. */
  readonly truncated: boolean;
}

/**
 * ⛔ **Returns the ENVELOPE, not the array** (DECISIONS R-24 D-4(b)). Unwrapping
 * here would destroy `truncated` at the client boundary — the UI would *receive* a
 * truncation signal and throw it away, which is the advertised-and-discarded shape
 * this whole program exists to remove. The panes destructure `.items`.
 *
 * ⛔ **The unwrap is DEFENSIVE** (R-24 H-2). These bodies are not zod-parsed
 * client-side, and a bare array is genuinely reachable — a server rollback, a stale
 * pod mid-deploy, a cached response, or the dev fixture before §3.5.5 lands. A bare
 * `body.items` against an array is `undefined`, and `undefined.map` is a blank pane.
 * `Array.isArray` first, `?? []` second, so neither shape can produce a throw.
 *
 * `cursor` reads the page after the one that returned it, through {@link pinHistoryPath}. A failed
 * read rejects with the client's `ApiError`, as every read in this file does.
 */
export async function getPinHistory(appId: string, limit = 50, cursor?: string): Promise<PinHistoryPage> {
  /*
   * ⛔⛔ **`AppPinHistoryEntry[]`, NOT `readonly AppPinHistoryEntry[]` — a COMPILER constraint, not
   * a style choice.** `Array.isArray` is declared `(arg: unknown) => arg is any[]`, and
   * `readonly T[]` is NOT assignable to `any[]`, so TS cannot remove a `readonly T[]` arm from a
   * union in the ELSE branch. Written with `readonly` here, the three property reads below are
   * three `TS2339 Property … does not exist` errors.
   * ⇒ Keep the LOCAL wire union mutable so `Array.isArray` narrows; the EXPORTED `PinHistoryPage`
   * keeps `readonly items`, and a mutable array widens to a readonly one for free.
   * ⛔ Do NOT "fix" this with `as unknown as` — a banned pattern (CLAUDE.md), and it would silence
   * the narrowing failure rather than resolve it.
   */
  const body = await api.get<AppPinHistoryEntry[] | Partial<PinHistoryPage>>(
    pinHistoryPath(appId, limit, cursor),
  );
  if (Array.isArray(body)) {
    // Legacy/rollback shape: an array carries no truncation information, and
    // claiming `truncated: false` is the honest reading — the server that sent it
    // had no way to say otherwise.
    return { items: body, nextCursor: null, truncated: false };
  }
  return {
    items: body.items ?? [],
    nextCursor: body.nextCursor ?? null,
    truncated: body.truncated ?? false,
  };
}

export function rolloutDiagnosticsPath(appId: string): string {
  return `/v1/apps/${appId}/rollout-diagnostics`;
}

export function getRolloutDiagnostics(appId: string): Promise<AppRolloutDiagnostics> {
  return api.get<AppRolloutDiagnostics>(rolloutDiagnosticsPath(appId));
}

/** ⛔ Every write below is confirm-then-act at the CALL SITE (§3.3 / §6 assertion 8). */
export function promote(appId: string, body: PromoteRequest): Promise<unknown> {
  return api.post<unknown>(`/v1/apps/${appId}/promote`, body);
}

export function demote(appId: string, body: DemoteRequest): Promise<unknown> {
  return api.post<unknown>(`/v1/apps/${appId}/demote`, body);
}

/**
 * Remove the pin on one ring.
 *
 * ⛔ The path segment is the **ring name** (`stable`/`beta`/`alpha`/`edge`), NOT a channel
 * uuid — see this file's header. Returns 204, so the response body is `undefined`.
 */
export function unpin(appId: string, ring: Ring): Promise<void> {
  return api.delete<void>(`/v1/apps/${appId}/channel-pins/${encodeURIComponent(ring)}`);
}

/**
 * Pin a version to a channel — R7 WS-4 E63, the **other half** of `unpin` above.
 *
 * ## ⛔ THE ASYMMETRY, AND WHY IT IS THE MOST DANGEROUS THING ON THIS SCREEN
 *
 * This file's header already recorded it; E63 is where it becomes executable:
 *
 * | | path segment | permission | controller |
 * |---|---|---|---|
 * | **pin** (this) | the **channel UUID** | `cp:app:write` | `AppChannelPinsController.set` |
 * | **unpin** (above) | the **channel NAME** | `cp:app:channel:pin` | `AppPromotionController.unpin` |
 *
 * Two controllers, two key kinds, two permissions, **one URL family** — they differ only by HTTP
 * verb, so nothing at the routing layer separates them. ⛔ A UI that reuses one key for both breaks
 * exactly half of it, and the broken half is the one nobody clicks in review: sending a channelId
 * to the unpin route 404s, and sending a name here 404s too. Both fail silently as "nothing
 * happened". WS-4 A10 asserts **both literal URLs** for that reason.
 *
 * ⚠️ `channelId` comes from `GET /v1/channels` (E56, `channels.api.ts`) — the persisted rows are the
 * only source of channel **identity**. `GET /v1/platform/rings` (E57) carries the four names and
 * descriptions but **no ids**, so it cannot serve this call. That is why WS-4 §1.4 says both reads
 * are needed: rings for vocabulary, channels for identity.
 *
 * ⚠️ `pinnedVersionId` is an **`AppComposition` row id** (`release.app_compositions.id`), not a
 * version string and not a registry-version id — read off the request schema's own `.describe()`
 * (`updateAppChannelPinSchema`). Passing a human-readable version would pass zod's
 * `.uuid()` only if it happened to be a uuid, and then resolve to nothing.
 *
 * Returns the updated pin (`AppChannelPinView`, 200 — not 204 like unpin).
 */
export function pinPath(appId: string, channelId: string): string {
  return `/v1/apps/${appId}/channel-pins/${encodeURIComponent(channelId)}`;
}

export function pin(
  appId: string,
  channelId: string,
  pinnedVersionId: string,
): Promise<AppChannelPin> {
  return api.put<AppChannelPin>(pinPath(appId, channelId), { pinnedVersionId });
}
